import { Link } from "react-router-dom";
import {
  Button,
  Heading,
  Text,
  Box,
  Image,
  Stack,
  Badge,
  useColorModeValue,
  Icon,
  HStack,
  Divider,
  Tooltip,
} from "@chakra-ui/react";
// icons
import { ImLocation } from "react-icons/im";
import { FaBluetooth, FaCarCrash, FaUsers, FaRoute, FaCalendarAlt, FaClock, FaMoneyBillWave, FaExchangeAlt } from "react-icons/fa";
import { GiCarSeat } from "react-icons/gi";
import { AiTwotoneDelete } from "react-icons/ai";
import { EditIcon } from "@chakra-ui/icons";
import { MdElectricRickshaw, MdLocalTaxi } from "react-icons/md";

import moment from "moment";
import "./ListingItem.css";

const ListingItem = ({ data, id, sellerId, deleteListing, editListing }) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const shadowColor = useColorModeValue(
    "0 6px 10px rgba(0, 0, 0, 0.08)",
    "0 6px 10px rgba(0, 0, 0, 0.3)"
  );

  // Helper function to get listing type badge color
  const getListingTypeBadge = (type) => {
    switch (type) {
      case "sale":
        return { color: "green", label: "For Sale", icon: MdElectricRickshaw };
      case "rent":
        return { color: "orange", label: "For Rent", icon: MdLocalTaxi };
      case "ride":
        return { color: "purple", label: "Ride Sharing", icon: FaUsers };
      default:
        return { color: "blue", label: "Listing", icon: MdElectricRickshaw };
    }
  };

  const listingType = getListingTypeBadge(data.type);

  // Format price based on listing type
  const getPriceDisplay = () => {
    if (data.type === "ride") {
      return {
        label: "Per Seat",
        price: data.price,
        suffix: "/seat"
      };
    } else if (data.type === "rent") {
      return {
        label: "Per Day",
        price: data.price,
        suffix: "/day"
      };
    } else {
      return {
        label: "Price",
        price: data.offer ? data.discountedPrice : data.price,
        suffix: ""
      };
    }
  };

  const priceDisplay = getPriceDisplay();

  // Format date for ride listings
  const formatDate = (dateString) => {
    if (!dateString) return "TBD";
    return moment(dateString).format("MMM D, YYYY");
  };

  // Get the details page URL
  const getDetailsUrl = () => {
    return sellerId
      ? `/category/${data.type || "sale"}/${id}?sellerId=${sellerId || data.sellerId}&type=${data.type || "featured-seller-listing"}`
      : `/category/${data.type || "sale"}/${id}`;
  };

  return (
    <div
      className={`relative flex flex-col mx-1 md:flex-row p-5 gap-5 w-full mb-6 sm:w-11/12 sm:mx-auto rounded-xl transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl border border-opacity-10 border-gray-300`}
      style={{
        boxShadow: shadowColor,
        backdropFilter: "blur(4px)",
        backgroundColor: cardBg,
      }}
    >
      {/* Image Section - Clickable to navigate to details */}
      <Link to={getDetailsUrl()} className="w-full md:w-[240px] lg:w-[300px] h-[190px] relative group overflow-hidden rounded-xl bg-gray-200 block">
        <Image
          className="w-full h-full object-cover absolute transition-transform duration-500 group-hover:scale-105"
          src={data.carImages?.[0] || "/placeholder-car.jpg"}
          alt={`${data.manufacturer} ${data.modelName}`}
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

        {/* Badges */}
        <div className="absolute top-2 right-2 flex flex-col space-y-1">
          {data.offer && data.type === "sale" && <Badge colorScheme="red">Special Offer</Badge>}
          {data.featured && <Badge colorScheme="blue">Featured</Badge>}
          <Badge colorScheme={listingType.color} fontSize="xs" className="capitalize">
            {listingType.label}
          </Badge>
        </div>
      </Link>

      {/* Details Section */}
      <div className="flex-1 relative space-y-2">
        <div className="flex items-start justify-between">
          <div>
            <Heading as="h4" size="sm" fontWeight="bold" noOfLines={1}>
              {data.modelName} {data.modelYear}
            </Heading>
            <Heading as="h5" size="xs" className="text-blue-600" noOfLines={1}>
              {data.manufacturer}
            </Heading>
          </div>
          <Badge colorScheme="green" fontSize="0.75em">
            {data?.timestamp ? moment(data?.timestamp?.toDate()).fromNow() : ""}
          </Badge>
        </div>

        {/* Location */}
        <Text fontSize="sm" className="flex items-center text-gray-600" noOfLines={1}>
          <ImLocation className="inline mr-1 text-blue-500" />
          {data.city}
        </Text>

        {/* Exchange Badge for Sale Listings */}
        {data.type === "sale" && data.exchangeAvailable && (
          <Tooltip label="Seller accepts exchange for another car" placement="top">
            <Badge colorScheme="purple" fontSize="xs" className="inline-flex items-center gap-1 px-2 py-1">
              <FaExchangeAlt className="text-xs" />
              Exchange Available
            </Badge>
          </Tooltip>
        )}

        {/* Ride-specific details */}
        {data.type === "ride" && (
          <Box className="space-y-2">
            {/* Route */}
            <HStack spacing={2} className="text-sm">
              <Icon as={FaRoute} className="text-purple-500" />
              <Text fontWeight="medium">
                {data.fromCity || "Pickup"} → {data.toCity || "Dropoff"}
              </Text>
              {data.rideType === "round-trip" && (
                <Badge colorScheme="purple" size="sm">
                  Round Trip
                </Badge>
              )}
            </HStack>

            {/* Departure Details */}
            <HStack spacing={4} className="text-sm text-gray-600">
              <HStack spacing={1}>
                <Icon as={FaCalendarAlt} className="text-purple-400" />
                <Text>{formatDate(data.departureDate)}</Text>
              </HStack>
              <HStack spacing={1}>
                <Icon as={FaClock} className="text-purple-400" />
                <Text>{data.departureTime || "TBD"}</Text>
              </HStack>
              <HStack spacing={1}>
                <Icon as={FaUsers} className="text-purple-400" />
                <Text>{data.availableSeats || 4} seats left</Text>
              </HStack>
            </HStack>

            {/* Ride Preferences */}
            {(data.smoking || data.pets || data.femaleOnly) && (
              <HStack spacing={2} className="text-xs flex-wrap">
                {data.smoking && <Badge colorScheme="gray">🚭 No Smoking</Badge>}
                {data.pets && <Badge colorScheme="gray">🐾 Pets Allowed</Badge>}
                {data.femaleOnly && <Badge colorScheme="pink">👩‍🦰 Female Only</Badge>}
              </HStack>
            )}
          </Box>
        )}

        {/* Features - Only show for sale/rent */}
        {data.type !== "ride" && (
          <Stack direction="row" spacing="10px" className="mt-1 flex-wrap">
            {[
              { icon: <GiCarSeat />, condition: data.options?.leatherSeats, text: "Leather" },
              { icon: <FaBluetooth />, condition: data.options?.bluetooth, text: "Bluetooth" },
              { icon: <FaCarCrash />, condition: data.options?.airBags, text: "Airbags" }
            ].map((feature, i) => (
              <Box
                key={i}
                className={`px-2 py-1 rounded-md text-xs ${feature.condition ? 'bg-blue-50' : 'bg-gray-100 opacity-60'}`}
              >
                <Text as={feature.condition ? "" : "s"} className="flex items-center gap-1">
                  {feature.icon} {feature.text}
                </Text>
              </Box>
            ))}
          </Stack>
        )}

        {/* Exchange Preferences - For Sale Listings */}
        {data.type === "sale" && data.exchangeAvailable && (data.exchangeManufacturer || data.exchangeModel) && (
          <Box className="mt-2 p-2 bg-purple-50 rounded-md border border-purple-200">
            <Text className="text-xs font-semibold text-purple-700 flex items-center gap-1 mb-1">
              <FaExchangeAlt className="text-xs" />
              Looking to exchange with:
            </Text>
            <div className="flex flex-wrap gap-2 text-xs">
              {data.exchangeManufacturer && (
                <Badge colorScheme="purple" variant="subtle" fontSize="xs">
                  {data.exchangeManufacturer}
                </Badge>
              )}
              {data.exchangeModel && (
                <Badge colorScheme="purple" variant="subtle" fontSize="xs">
                  {data.exchangeModel}
                </Badge>
              )}
              {data.exchangeYear && (
                <Badge colorScheme="purple" variant="subtle" fontSize="xs">
                  {data.exchangeYear}
                </Badge>
              )}
              {data.exchangeCondition && (
                <Badge colorScheme="purple" variant="subtle" fontSize="xs" className="capitalize">
                  {data.exchangeCondition}
                </Badge>
              )}
            </div>
          </Box>
        )}

        {/* Specifications - Show for all types */}
        <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
          <Box className="p-1.5 bg-gray-50 rounded-md">
            <Text><b>Mileage:</b> {data.mileage?.toLocaleString() || 0} km</Text>
          </Box>
          <Box className="p-1.5 bg-gray-50 rounded-md">
            <Text><b>Transmission:</b> {data.transmission || "Automatic"}</Text>
          </Box>
        </div>

        {/* Additional ride info */}
        {data.type === "ride" && data.luggageSpace && (
          <Box className="p-1.5 bg-gray-50 rounded-md text-sm">
            <Text><b>Luggage Space:</b> {data.luggageSpace}</Text>
          </Box>
        )}
      </div>

      {/* Price & Actions */}
      <div className="basis-1/4 flex flex-col justify-between text-sm">
        <Box className="p-3 bg-blue-50 rounded-md text-center">
          <Text className="text-xs text-gray-600">{priceDisplay.label}</Text>
          <Text fontSize="lg" fontWeight="bold" className="text-blue-700">
            ₨ {priceDisplay.price?.toLocaleString()}
            {priceDisplay.suffix && (
              <Text as="span" fontSize="xs" className="text-gray-500 ml-1">
                {priceDisplay.suffix}
              </Text>
            )}
          </Text>
          {data.type === "sale" && data.offer && (
            <Text className="text-xs line-through text-gray-500">
              ₨ {data.price?.toLocaleString()}
            </Text>
          )}
          {data.type === "sale" && data.installmentsAvailable && (
            <Tooltip label="Pay in easy installments" placement="top">
              <Text className="text-xs text-green-600 mt-1">
                ✅ Installments available
              </Text>
            </Tooltip>
          )}
          {data.type === "sale" && data.exchangeAvailable && (
            <Tooltip label="Exchange your car for this one" placement="top">
              <Text className="text-xs text-purple-600 mt-1">
                🔄 Exchange possible
              </Text>
            </Tooltip>
          )}
          {data.type === "ride" && data.availableSeats && (
            <Text className="text-xs text-purple-600 mt-1">
              {data.availableSeats} seats available
            </Text>
          )}
        </Box>

        <Stack spacing={2} mt={2}>
          <Button
            as={Link}
            to={getDetailsUrl()}
            size="sm"
            colorScheme="blue"
            borderRadius="md"
            className="transition-all duration-200 transform hover:scale-105"
          >
            View Details
          </Button>

          {editListing && (
            <Link to={`/edit-listing/${id}`}>
              <Button
                size="sm"
                variant="outline"
                colorScheme="blue"
                leftIcon={<EditIcon />}
              >
                Edit
              </Button>
            </Link>
          )}
        </Stack>

        {deleteListing && (
          <button
            onClick={() => deleteListing(data.id)}
            className="absolute top-1 right-1 p-1 text-red-500 hover:text-red-700 transition-colors duration-200 transform hover:scale-105"
            aria-label="Delete listing"
          >
            <AiTwotoneDelete size={20} />
          </button>
        )}
      </div>
    </div>
  );
};

export default ListingItem;